import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function MessagesContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">消息</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 消息系统的完整流程，包括去重、防抖、队列、流式传输等核心机制。
        </p>
      </div>

      <SectionTitle id="msg-flow">消息流程（高层概述）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        一条消息从用户发送到智能体回复的完整流程：
      </p>
      <div className="space-y-2 my-4">
        {[
          ['渠道接收', '渠道适配器从平台接收原始消息'],
          ['去重检查', '检查消息 ID，过滤重复消息'],
          ['防抖处理', '等待短暂延迟，合并快速连续的消息'],
          ['消息入队', '将消息放入对应会话的处理队列'],
          ['会话加载', '加载会话历史和工作区状态'],
          ['智能体循环', '执行完整的智能体处理循环'],
          ['回复发送', '将生成的回复发送回渠道'],
          ['会话保存', '更新并保存会话状态'],
        ].map(([title, desc], i) => (
          <div key={title} className="flex items-center gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0">{i + 1}</span>
            <div className="flex-1 flex items-center gap-2 bg-slate-800/40 border border-slate-700/50 rounded-lg px-3 py-2">
              <span className="text-white font-medium text-sm w-20 flex-shrink-0">{title}</span>
              <span className="text-slate-400 text-xs">{desc}</span>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="msg-dedup">入站去重</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每条消息都有唯一的消息 ID，Gateway 会记录已处理的消息 ID，防止重复处理：
      </p>
      <CodeBlock language="json5" code={`{
  gateway: {
    deduplication: {
      // 是否启用去重
      enabled: true,
      // 去重窗口时间（秒）
      windowSeconds: 60,
      // 去重缓存大小（条目数）
      cacheSize: 10000,
    }
  }
}`} />
      <InfoBox type="info">
        去重对于 WhatsApp 等可能重复投递消息的渠道尤为重要。
      </InfoBox>

      <SectionTitle id="msg-debounce">入站防抖</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        防抖机制会等待用户停止输入后再处理消息，避免对每个字符都触发处理：
      </p>
      <CodeBlock language="json5" code={`{
  gateway: {
    debounce: {
      // 防抖延迟（毫秒）
      delayMs: 1500,
      // 最大等待时间（毫秒），超过后强制处理
      maxWaitMs: 5000,
    }
  }
}`} />
      <InfoBox type="tip">
        适当的防抖延迟可以让用户完整输入多行消息后再触发处理，提升体验。建议设置为 1000-2000ms。
      </InfoBox>

      <SectionTitle id="msg-session-device">会话和设备</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        同一用户可能从多个设备发送消息，OpenClaw 通过对等方 ID 而非设备 ID 来识别用户，确保跨设备的会话一致性：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-smartphone-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>同一用户从手机和电脑发送的消息共享同一个会话</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-smartphone-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>设备信息作为元数据保存，但不影响会话路由</span></li>
      </ul>

      <SectionTitle id="msg-body-history">入站正文和历史上下文</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        消息正文处理包括：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-text-wrap text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">文本提取：</strong>从各平台格式中提取纯文本内容</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-image-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">媒体处理：</strong>图片、文件等媒体内容的下载和描述</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-reply-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">引用注入：</strong>将被引用的消息内容注入到上下文中</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-history-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span><strong className="text-white">历史注入：</strong>首次加入群组时注入历史消息</span></li>
      </ul>

      <SectionTitle id="msg-queue">队列和后续消息</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个会话有独立的消息队列，处理规则如下：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    queue: {
      // 队列最大深度
      maxDepth: 10,
      // 队列满时的策略
      overflowPolicy: "drop",  // drop / wait / replace-last
      // 是否允许中断当前处理
      allowInterrupt: false,
      // 后续消息处理策略
      subsequentPolicy: "queue",  // queue / drop / interrupt
    }
  }
}`} />

      <SectionTitle id="msg-streaming">流式传输 / 分块和批处理</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 支持多种消息发送模式：
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
        {[
          { title: '流式传输', icon: 'ri-live-line', desc: '实时发送生成内容，用户看到打字效果', color: 'text-cyan-400' },
          { title: '分块发送', icon: 'ri-stack-line', desc: '按固定大小分块，平衡实时性和稳定性', color: 'text-teal-400' },
          { title: '批量发送', icon: 'ri-send-plane-line', desc: '生成完成后一次性发送，适合短回复', color: 'text-emerald-400' },
        ].map((item) => (
          <div key={item.title} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
            <div className="w-8 h-8 flex items-center justify-center mb-2">
              <i className={`${item.icon} ${item.color} text-lg`}></i>
            </div>
            <div className="text-white font-medium text-sm mb-1">{item.title}</div>
            <div className="text-xs text-slate-400">{item.desc}</div>
          </div>
        ))}
      </div>

      <SectionTitle id="msg-reasoning">推理可见性和 token</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        对于支持推理（Thinking）的模型（如 Claude 3.7），可以配置推理内容的可见性：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    reasoning: {
      // 是否显示推理过程给用户
      visible: false,
      // 推理 token 预算
      budgetTokens: 5000,
      // 推理内容前缀（显示时使用）
      prefix: "💭 思考中...\n",
    }
  }
}`} />

      <SectionTitle id="msg-prefix-thread">前缀 / 线程和回复</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        消息前缀和线程回复配置：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    reply: {
      // 回复前缀（每条回复前添加）
      prefix: "",
      // 是否在线程中回复（支持的渠道）
      useThread: true,
      // 是否引用原始消息
      quoteOriginal: false,
      // 长消息分割策略
      splitLongMessages: true,
      // 单条消息最大长度（字符）
      maxMessageLength: 4096,
    }
  }
}`} />
    </div>
  );
}
